import React from 'react';
import { SectionHeading } from '../ui/SectionHeading';
import { Card } from '../ui/Card';
import { motion } from 'framer-motion';
import { DatabaseIcon, TargetIcon } from 'lucide-react';
import { useLang } from '../../lib/i18n/LanguageContext';
import { dictionary } from '../../lib/i18n/dictionary';
const sources = ['API Composite List', 'Rigzone Directory', 'EPA Natural Gas STAR Provider Directory', 'Thomasnet supplier categories', 'GlobalSpec industrial product search', 'LinkedIn Sales Navigator', 'Houston oilfield equipment suppliers', 'Louisiana oil and gas association/member directories', 'Texas/Louisiana industrial distributors', 'Existing export/logistics companies serving Latin America'];
const categoryBreakdown = {
  en: [{
    count: 15,
    label: 'Valve, choke, manifold, and flow control companies'
  }, {
    count: 10,
    label: 'Pump and compressor companies'
  }, {
    count: 10,
    label: 'Generator, transformer, and electrical infrastructure companies'
  }, {
    count: 10,
    label: 'Automation, monitoring, and industrial controls companies'
  }, {
    count: 10,
    label: 'Methane detection and environmental technology companies'
  }, {
    count: 10,
    label: 'Refinery maintenance and repair companies'
  }, {
    count: 10,
    label: 'Logistics, freight forwarding, and customs support companies'
  }, {
    count: 10,
    label: 'API or ISO certified equipment manufacturers'
  }, {
    count: 5,
    label: 'Industrial safety equipment companies'
  }, {
    count: 5,
    label: 'Inspection, testing, and certification companies'
  }, {
    count: 5,
    label: 'Spanish speaking or Latin America export ready companies'
  }],
  es: [{
    count: 15,
    label: 'Empresas de válvulas, chokes, múltiples y control de flujo'
  }, {
    count: 10,
    label: 'Empresas de bombas y compresores'
  }, {
    count: 10,
    label: 'Empresas de generadores, transformadores e infraestructura eléctrica'
  }, {
    count: 10,
    label: 'Empresas de automatización, monitoreo y controles industriales'
  }, {
    count: 10,
    label: 'Empresas de detección de metano y tecnología ambiental'
  }, {
    count: 10,
    label: 'Empresas de mantenimiento y reparación de refinerías'
  }, {
    count: 10,
    label: 'Empresas de logística, transitario y soporte aduanal'
  }, {
    count: 10,
    label: 'Fabricantes de equipos certificados API o ISO'
  }, {
    count: 5,
    label: 'Empresas de equipos de seguridad industrial'
  }, {
    count: 5,
    label: 'Empresas de inspección, pruebas y certificación'
  }, {
    count: 5,
    label: 'Empresas de habla hispana o listas para exportar a América Latina'
  }]
};
export function SupplierBuildStrategy() {
  const {
    lang
  } = useLang();
  const t = dictionary.strategy;
  const m = t.metrics;
  const tracker = [{
    label: m.target[lang],
    value: 100,
    accent: true
  }, {
    label: m.verified[lang],
    value: 0
  }, {
    label: m.inResearch[lang],
    value: 0
  }, {
    label: m.contacted[lang],
    value: 0
  }, {
    label: m.interested[lang],
    value: 0
  }, {
    label: m.approved[lang],
    value: 0
  }];
  return <section id="strategy" className="py-24 md:py-32 bg-slate-50" data-id="element-575">
      <div className="max-w-7xl mx-auto px-5 md:px-8" data-id="element-576">
        <SectionHeading eyebrow={t.eyebrow[lang]} title={t.title[lang]} subtitle={t.subtitle[lang]} data-id="element-577" />

        <Card className="mt-12 p-6 md:p-8" data-id="element-578">
          <div className="flex items-center gap-2.5 mb-5" data-id="element-579">
            <TargetIcon className="w-4 h-4 text-gold-deep" data-id="element-580" />
            <span className="font-mono text-[11px] tracking-[0.2em] uppercase text-gold-deep" data-id="element-581">
              {t.liveProgress[lang]}
            </span>
          </div>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-4 md:gap-6" data-id="element-582">
            {tracker.map(it => <div key={it.label} data-id="element-583">
                <div className={`font-serif text-3xl md:text-4xl ${it.accent ? 'text-gold' : 'text-navy-deep'}`} data-id="element-584">
                  {it.value}
                </div>
                <div className="font-mono text-[10.5px] tracking-wider text-slate-500 uppercase mt-1" data-id="element-585">
                  {it.label}
                </div>
              </div>)}
          </div>
          <div className="mt-6 h-1.5 bg-slate-200 rounded-full overflow-hidden" data-id="element-586">
            <motion.div initial={{
            width: 0
          }} whileInView={{
            width: '0%'
          }} viewport={{
            once: true
          }} transition={{
            duration: 1.2,
            ease: 'easeOut'
          }} className="h-full bg-gold" data-id="element-587" />
          </div>
          <p className="mt-3 font-mono text-[10.5px] text-slate-500" data-id="element-588">
            {t.networkInit[lang]}
          </p>
        </Card>

        <div className="mt-8 grid lg:grid-cols-2 gap-5" data-id="element-589">
          <Card className="p-7 md:p-8" delay={0.05} data-id="element-590">
            <div className="flex items-center gap-2.5 mb-5" data-id="element-591">
              <DatabaseIcon className="w-4 h-4 text-petroleum" data-id="element-592" />
              <h3 className="font-serif text-xl text-navy-deep" data-id="element-593">
                {t.sourcesTitle[lang]}
              </h3>
            </div>
            <ul className="space-y-2.5" data-id="element-594">
              {sources.map((s, i) => <li key={s} className="flex items-start gap-3 text-[14px] text-slate-700" data-id="element-595">
                  <span className="font-mono text-[10.5px] text-slate-400 mt-1 w-6" data-id="element-596">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <span data-id="element-597">{s}</span>
                </li>)}
            </ul>
          </Card>

          <Card className="p-7 md:p-8" delay={0.12} data-id="element-598">
            <div className="flex items-center justify-between mb-5" data-id="element-599">
              <h3 className="font-serif text-xl text-navy-deep" data-id="element-600">
                {t.categoriesTitle[lang]}
              </h3>
              <span className="font-mono text-[11px] text-gold-deep" data-id="element-601">
                = 100
              </span>
            </div>
            <ul className="space-y-2" data-id="element-602">
              {categoryBreakdown[lang].map(c => <li key={c.label} className="flex items-start gap-4 py-1.5 border-b border-slate-100 last:border-0" data-id="element-603">
                  <span className="font-serif text-xl text-gold w-8 text-right shrink-0" data-id="element-604">
                    {c.count}
                  </span>
                  <span className="text-[13.5px] text-slate-700 leading-snug" data-id="element-605">
                    {c.label}
                  </span>
                </li>)}
            </ul>
          </Card>
        </div>
      </div>
    </section>;
}